// The frame every page sits in (E12-S1-T1): a top bar with the product name and logo, the
// tenant's name, the navigation and the account menu, then the outlet. Ported from the Container
// posture's title bar and workspace tabs (mockups/container.html, container.css .titlebar, .tab):
// the same panel, hairlines, type and active-tab treatment, with the hub's destinations in place of
// open apps. Signed out, or before the session is known, the bar shows the brand alone.
import type { Principal } from "@waypoint/contracts";
import { cn, focusRing } from "@waypoint/surface";
import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { isTenantAdmin } from "../auth/session-state.js";
import { t, type PlainKey } from "../i18n/t.js";
import { AccountMenu } from "./account-menu.js";
import { Brand } from "./brand.js";

interface NavItem {
  readonly href: string;
  readonly label: PlainKey;
}

const NAV: readonly NavItem[] = [
  { href: "/learn", label: "nav.learn" },
  { href: "/library", label: "nav.library" },
  { href: "/practice", label: "nav.practice" },
  { href: "/record", label: "nav.record" },
];

const PLAN: NavItem = { href: "/plan", label: "nav.plan" };
const ADMIN: NavItem = { href: "/admin", label: "nav.admin" };
const ISSUES: NavItem = { href: "/issues", label: "nav.issues" };
const SCRIPTS: NavItem = { href: "/scripts", label: "nav.scripts" };

/**
 * The hub's destinations for this principal: Admin only for a tenant administrator, Source issues
 * for the client reviewer and the guide's owners (a tenant administrator can read the queue too,
 * by its address, but it is not one of their destinations), and Scripts for content owners, who alone
 * may read title versions (content.author).
 */
export function navFor(principal: Principal): readonly NavItem[] {
  const issues =
    principal.roles.includes("client-reviewer") || principal.roles.includes("content-owner");
  const plan = issues || isTenantAdmin(principal);
  return [
    ...NAV,
    ...(issues ? [ISSUES] : []),
    ...(plan ? [PLAN] : []),
    ...(principal.roles.includes("content-owner") ? [SCRIPTS] : []),
    ...(isTenantAdmin(principal) ? [ADMIN] : []),
  ];
}

function isActive(location: string, href: string): boolean {
  if (href === "/issues")
    return (
      location === href ||
      location.startsWith("/issues/") ||
      /^\/packs\/[^/]+\/issues$/.test(location)
    );
  if (href === "/scripts")
    return (
      location === href || location.startsWith("/scripts/") || location.startsWith("/content/")
    );
  return location === href || location.startsWith(`${href}/`);
}

/** The queue and the script review are two columns and want more room than the hub's other pages. */
function isWide(location: string): boolean {
  return isActive(location, "/issues") || isActive(location, "/scripts");
}

function Navigation({ items }: { items: readonly NavItem[] }) {
  const [location] = useLocation();
  return (
    <nav
      aria-label={t("nav.label")}
      className="order-last w-full min-w-0 overflow-x-auto pb-2 md:order-none md:w-auto md:pb-0"
    >
      <ul className="flex items-center gap-1">
        {items.map((item) => {
          const active = isActive(location, item.href);
          return (
            <li key={item.href}>
              <Link
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex h-7 items-center rounded-8 border px-2.5 text-13 font-medium whitespace-nowrap",
                  "transition duration-fast ease-wp",
                  active
                    ? "border-wp-line-strong bg-wp-bg text-wp-text"
                    : "border-transparent text-wp-text-2 hover:bg-br-sky/9 hover:text-wp-text",
                  focusRing,
                )}
              >
                {t(item.label)}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function Frame({
  principal,
  children,
}: {
  principal?: Principal | undefined;
  children: ReactNode;
}) {
  const [location] = useLocation();
  return (
    <div className="flex min-h-screen flex-col bg-wp-bg-2 font-sans text-13 leading-body text-wp-text">
      <a
        href="#content"
        className={cn(
          "sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-modal",
          "focus:rounded-8 focus:bg-wp-panel focus:px-3 focus:py-2",
          focusRing,
        )}
      >
        {t("app.skipToContent")}
      </a>
      <header className="flex shrink-0 flex-wrap items-center gap-x-4 border-b border-wp-line bg-wp-panel px-3.5 md:h-12 md:flex-nowrap">
        {/* Narrow screens: brand and account on the first row, the navigation scrolls on a second. */}
        <div className="flex h-12 min-w-0 shrink-0 items-center gap-2">
          <Brand />
        </div>
        {principal ? (
          <>
            <Navigation items={navFor(principal)} />
            <span className="grow" />
            <AccountMenu principal={principal} />
          </>
        ) : null}
      </header>
      <main id="content" tabIndex={-1} className="flex-1 outline-none">
        <div
          className={cn(
            "mx-auto w-full px-4 py-6 sm:px-6",
            isWide(location) ? "max-w-330" : "max-w-5xl",
          )}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
